import React, { FC, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import epcisService from 'services/api/epcis.service';

export interface OutboundValidationResult {
    compliant?: boolean;
    status?: string;
    violations?: string[];
}

interface Props {
    epcisId: number;
    compact?: boolean;
    onResult?: (result: OutboundValidationResult | null) => void;
}

const OutboundValidationPanel: FC<Props> = ({ epcisId, compact = false, onResult }) => {
    const [result, setResult] = useState<OutboundValidationResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const res: any = await epcisService.getOutboundValidation(epcisId);
            setResult(res.data);
            onResult?.(res.data);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Validation request failed');
            setResult(null);
            onResult?.(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (epcisId) {
            load();
        }
    }, [epcisId]);

    const failed = result?.compliant === false || result?.status === 'FAILED';
    const passed = result?.compliant === true || result?.status === 'PASSED';

    return (
        <Box
            className={`rounded-md border p-3 ${
                failed ? 'border-red-200 bg-red-50' : passed ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'
            }`}
        >
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <Typography variant="subtitle2" className="font-semibold text-slate-800">
                    Outbound L4 validation
                </Typography>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="rounded border border-slate-300 px-2 py-0.5 text-xs hover:bg-white disabled:opacity-50"
                >
                    {loading ? 'Checking…' : 'Refresh'}
                </button>
            </div>
            {error ? <Typography color="error" variant="body2">{error}</Typography> : null}
            {!error && result ? (
                <>
                    <Typography variant="body2" className={failed ? 'text-red-800' : 'text-green-800'}>
                        Status: <strong>{result.status || (result.compliant ? 'PASSED' : 'FAILED')}</strong>
                    </Typography>
                    {failed && result.violations && result.violations.length > 0 ? (
                        <ul className={`mt-2 list-disc pl-5 text-sm text-red-800 ${compact ? 'max-h-24 overflow-y-auto' : ''}`}>
                            {result.violations.map((v) => (
                                <li key={v}>{v}</li>
                            ))}
                        </ul>
                    ) : null}
                    {passed ? (
                        <Typography variant="caption" className="mt-1 block text-green-800">
                            SSCC commission → pack → ship choreography OK. Safe to dispatch.
                        </Typography>
                    ) : null}
                </>
            ) : null}
        </Box>
    );
};

export default OutboundValidationPanel;
